import { useMemo } from 'react'
import classnames from 'classnames/bind'
import moment from 'moment'
import { useTranslation } from 'next-i18next'

// Components
import Shell from '../Shell'
import ArticleList from '../ArticleList'
import Reaction from '../Reaction'

// Methods
import { prepareArticleHtml } from '../../methods'

// Types
import type { FetchHelpArticleResponse } from '@/api/fetchHelpArticle'

// Styles
import styles from '../../style.module.scss'

const cx = classnames.bind(styles)

type Props = {
  data: FetchHelpArticleResponse
  countryCode: string
  countryQuery?: string
}

const HelpArticle = ({ data, countryCode, countryQuery }: Props) => {
  const { t } = useTranslation()
  const { html, toc } = useMemo(() => prepareArticleHtml(data.content), [data.content])
  // 只有一個小標時目錄沒有意義
  const showToc = data.showToc && toc.length > 1

  return (
    <Shell breadcrumbs={data.breadcrumbs} countryCode={countryCode} countryQuery={countryQuery}>
      <div className={cx('help-article-layout', { 'help-article-layout--with-toc': showToc })}>
        <article className={cx('help-card', 'help-article')}>
          <h1 className={cx('help-article__title')}>{data.title}</h1>
          {data.summary && <p className={cx('help-article__summary')}>{data.summary}</p>}
          <p className={cx('help-article__updated')}>{t('Help_Center_Updated_At', { date: moment(data.updatedAt).format('YYYY/MM/DD') })}</p>

          {showToc && (
            <nav className={cx('help-toc', 'help-toc--inline')} aria-label={t('Help_Center_Toc')}>
              <p className={cx('help-toc__title')}>{t('Help_Center_Toc')}</p>
              <ol>
                {toc.map((item) => (
                  <li key={item.id}>
                    <a href={`#${item.id}`}>{item.text}</a>
                  </li>
                ))}
              </ol>
            </nav>
          )}

          <div className={cx('help-article__content')} dangerouslySetInnerHTML={{ __html: html }} />
          <Reaction articleId={data.id} countryCode={countryCode} />
        </article>

        {showToc && (
          <aside className={cx('help-article-layout__aside')}>
            <nav className={cx('help-toc', 'help-toc--aside')} aria-label={t('Help_Center_Toc')}>
              <p className={cx('help-toc__title')}>{t('Help_Center_Toc')}</p>
              <ol>
                {toc.map((item) => (
                  <li key={item.id}>
                    <a href={`#${item.id}`}>{item.text}</a>
                  </li>
                ))}
              </ol>
            </nav>
          </aside>
        )}
      </div>

      {data.relatedArticles.length > 0 && (
        <section className={cx('help-card', 'help-related')}>
          <h2 className={cx('help-card__title')}>{t('Help_Center_Related')}</h2>
          <ArticleList articles={data.relatedArticles} countryQuery={countryQuery} />
        </section>
      )}
    </Shell>
  )
}

export default HelpArticle
